<template>
  <StandardEmptyPageContainer>
    <v-row class="justify-center pt-5">
      <v-col cols="12" lg="8">
        <v-row class="text-left">
          <v-col>
            <h1>Add colleagues</h1>
          </v-col>
        </v-row>
        <v-row class="text-left">
          <v-col>
            <p>Users that you add will have access to answer and forward questionnaires. They will also have permission to remove other users from the team, including yourself.</p>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <StandardCardContainer>
              <template #title>
                Invite users
              </template>
              <v-container class="py-0">
                <v-row v-for="(user, index) in state.newUsers">
                  <v-col cols="10" md="11">
                    <v-row>
                      <v-col cols="12" sm="6" class="text-left">
                        <PInputField
                          label="Name"
                          :value="user.name"
                          @update-event="
                            (val) => {
                              user.name = val;
                            }
                          "
                        />
                      </v-col>
                      <v-col cols="12" sm="6" class="text-left">
                        <PInputField
                          label="Email"
                          :value="user.email"
                          :error="v.newUsers.$errors[0]?.$response.$errors[index].email.length > 0"
                          :error-messages="v.newUsers.$errors[0]?.$response.$errors[index].email[0]?.$message"
                          @update-event="
                            (val) => {
                              user.email = val;
                            }
                          "
                        />
                      </v-col>
                    </v-row>
                  </v-col>
                  <v-col align-self="center">
                    <AppkitIcon
                      style="cursor: pointer"
                      icon="icon-delete-outline"
                      @click="removeUser(user)"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col class="text-left">
                    <PTextButton @click="addEmptyUser">Add another</PTextButton>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-divider class="mb-6" />
                    <v-row>
                      <v-col class="text-right">
                        <PTextButton class="mr-3" @click="routeToHome">
                          Skip for now
                        </PTextButton>
                        <PActionButton
                          :append-icon="true"
                          :icon-size="'xs'"
                          appkit-icon="icon-right-chevron-fill"
                          @click="addUsers"
                          >Continue
                        </PActionButton>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-container>
            </StandardCardContainer>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </StandardEmptyPageContainer>
</template>

<script setup lang="ts">
import StandardEmptyPageContainer from "@/components/containers/StandardEmptyPageContainer.vue";
import PActionButton from "@/components/custom/buttons/PActionButton.vue";
import { useRouter } from "vue-router";
import { RouterPath } from "@/models/enum/routerPath";
import { ref, type Ref } from "vue";
import { TrustGroupUserDto } from "@/models/dto/trustGroupUserDto";
import { addUserToTrustGroup } from "@/services/trustGroupService";
import PInputField from "@/components/custom/PInputField.vue";
import PTextButton from "@/components/custom/buttons/PTextButton.vue";
import AppkitIcon from "@/components/custom/AppkitIcon.vue";
import { useSnackBarStore } from "@/stores/snackBarStore";
import { helpers, required, email } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import StandardCardContainer from "@/components/containers/StandardCardContainer.vue";
import { useAuthenticationStore } from "@/stores/authentication";

const router = useRouter();
const snackBarStore = useSnackBarStore();
const authenticationStore = useAuthenticationStore();
const newUsers: Ref<TrustGroupUserDto[]> = ref([new TrustGroupUserDto()]);

const state = ref({ newUsers });
const rules = {
  newUsers: {
    $each: helpers.forEach({
      email: {
        required, email
      }
    })
  }
};

const v = useVuelidate(rules, state);

function addEmptyUser(): void {
  state.value.newUsers.push(new TrustGroupUserDto());
}

function removeUser(user: TrustGroupUserDto): void {
  const index = state.value.newUsers.indexOf(user);
  state.value.newUsers.splice(index, 1);
}

async function addUsers(): Promise<void> {
  const validForm = await v.value.$validate();
  if (!validForm) {
    return;
  }

  for(var user of state.value.newUsers) {
    await addUser(user);
  }
  routeToHome();
}

async function addUser(user: TrustGroupUserDto): Promise<void> {
  try {
    await addUserToTrustGroup(user);
  } catch {
    snackBarStore.showError("Could not add one or more users.");
  }
}

function routeToHome() {
  authenticationStore.inOnboarding = false;
  router.push(RouterPath.Home);
}

</script>
